import { AnyAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { assignmentsClient } from '../../../api/client';
import {
  AssignmentCreateRequest,
  AssignmentRequestItemDeliver,
  AssignmentRequestUpdate,
  QueryAssignment,
  QueryAssignmentKPIs,
  QuerySiteStatusEntry,
  QuerySiteTimelineEvent,
  QueryVehicleManifest,
} from '../../../api/schema';
import { getPagination, keysToId } from '../../../helpers/sliceHelper';
import { RootState } from '../../store';
import { AssignmentsListType, AssignmentsState, AssignmentsTypeMapped, DeliverItemQuery, ListAssignmentsQuery } from './assignmentsInterface';

export const assignmentsAdapter = createEntityAdapter<AssignmentsListType>({
  selectId: (assignment) => assignment.id || '',
});

export const jobsiteAssignmentAdapter = createEntityAdapter<QueryAssignment>({
  selectId: (assignment) => assignment.job_site_id || '',
});

export const assignmentAdapter = createEntityAdapter<QueryAssignment>({
  selectId: (assignment) => assignment.id || '',
});

export const getJobsiteAssignment = createAsyncThunk('assignments/getJobsiteAssignment', async ({ siteId, options }: { siteId: string; options?: any }) =>
  assignmentsClient.getJobsiteAssignment(siteId, options),
);

export const getAssignmentById = createAsyncThunk('assignments/getAssignmentById', async (assigmentId: string) =>
  assignmentsClient.getAssignmentById(assigmentId),
);

export const createAssignment = createAsyncThunk('assignments/createAssignment', async (payload: AssignmentCreateRequest) =>
  assignmentsClient.createAssignment(payload),
);

export const lookupAssignmentByItem = createAsyncThunk(
  'assignments/lookupAssignmentByItem',
  async ({ identifier, options }: { identifier: string; options?: any }, { rejectWithValue }) => {
    try {
      return await assignmentsClient.lookupAssignmentByItem(identifier, options);
    } catch (error: any) {
      // Preserve the response body (status + field messages) so the pickup page can
      // distinguish "item not found" from "not in a prepared assignment" — the default
      // thunk serialization drops error.response.
      if (error?.response) {
        return rejectWithValue({ status: error.response.status, messages: error.response.data?.messages });
      }
      throw error;
    }
  },
);
export const lookupAssignmentItem = createAsyncThunk(
  'assignments/lookupAssignmentItem',
  async ({ assignmentId, identifier }: { assignmentId: string; identifier: string }) => assignmentsClient.lookupAssignmentItem(assignmentId, identifier),
);
export const updateAssignment = createAsyncThunk('assignments/updateAssignment', async (payload: AssignmentRequestUpdate) =>
  assignmentsClient.updateAssignment(payload),
);

export const takePossession = createAsyncThunk(
  'assignments/takePossession',
  async ({ assignmentId, options }: { assignmentId: string; options?: any }, { rejectWithValue }) => {
    try {
      return await assignmentsClient.takePossession(assignmentId, options);
    } catch (error: any) {
      // Preserve status + messages so the pickup page can distinguish
      // "already in your possession" (409) from other failures.
      if (error?.response) {
        return rejectWithValue({ status: error.response.status, messages: error.response.data?.messages });
      }
      throw error;
    }
  },
);

export const deliverAssignment = createAsyncThunk('assignments/deliverAssignment', async (assignmentId: string, { rejectWithValue }) => {
  try {
    return await assignmentsClient.deliverAssignment(assignmentId);
  } catch (error: any) {
    // Preserve status + messages so the field-service deliver page can
    // distinguish "not in your possession" (409) from other failures.
    if (error?.response) {
      return rejectWithValue({ status: error.response.status, messages: error.response.data?.messages });
    }
    throw error;
  }
});

export const getVehicleManifest = createAsyncThunk('assignments/getVehicleManifest', async (options?: any) => assignmentsClient.getVehicleManifest(options));

export const getJobSiteStatusBoard = createAsyncThunk('assignments/getJobSiteStatusBoard', async () => assignmentsClient.getJobSiteStatusBoard());

// tz is the viewer's zone: it decides the calendar day a Return is judged on.
export const getSiteActivityTimeline = createAsyncThunk('assignments/getSiteActivityTimeline', async ({ siteId, tz }: { siteId: string; tz: string }) =>
  assignmentsClient.getSiteActivityTimeline(siteId, tz),
);

export const possessItemByIdentifier = createAsyncThunk(
  'assignments/possessItemByIdentifier',
  async ({ identifier, options }: { identifier: string; options?: any }, { rejectWithValue }) => {
    try {
      return await assignmentsClient.possessItemByIdentifier(identifier, options);
    } catch (error: any) {
      // Preserve status + messages so the recover page can distinguish
      // "already on your truck" / "not at a job site" (409) from "not found" (404).
      if (error?.response) {
        return rejectWithValue({ status: error.response.status, messages: error.response.data?.messages });
      }
      throw error;
    }
  },
);

export const deleteAssignment = createAsyncThunk('assignments/deleteAssignment', async (assignmentId: string) =>
  assignmentsClient.deleteAssignment(assignmentId),
);

export const listAssignments = createAsyncThunk('assignments/listAssignments', async (query: ListAssignmentsQuery) => {
  const argumentsKeys = ['delivered', 'deliveredBegin', 'deliveredEnd', 'sortBy', 'offset', 'limit', 'options'];
  return assignmentsClient.listAssignments.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [
      delivered?: boolean,
      deliveredBegin?: string,
      deliveredEnd?: string,
      sortBy?: string,
      offset?: number,
      limit?: number,
      options?: Record<string, unknown>,
    ],
  );
});

export const deliverItem = createAsyncThunk('assignments/deliverItem', async (query: DeliverItemQuery) => {
  const argumentsKeys = ['itemId', 'deliveryTarget', 'options'];
  return assignmentsClient.deliverItem.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [itemId: string, deliveryTarget: AssignmentRequestItemDeliver, options?: Record<string, unknown>],
  );
});
export const deliverItemHome = createAsyncThunk('assignments/deliverItemHome', async (query: DeliverItemQuery) => {
  const argumentsKeys = ['itemId', 'deliveryTarget', 'options'];
  return assignmentsClient.deliverItemHome.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [itemId: string, deliveryTarget: AssignmentRequestItemDeliver, options?: Record<string, unknown>],
  );
});

export const getAssignmentKpis = createAsyncThunk('assignments/getAssignmentKpis', async () => {
  // Pass the viewer's browser-local start of day as a UTC instant; the backend applies
  // it as the delivered_cnt boundary only for Remediation organizations.
  const todayStart = DateTime.local().startOf('day').toUTC().toISO() ?? undefined;
  return assignmentsClient.getAssignmentKpis(todayStart);
});

export const assignmentsSlice = createSlice({
  name: 'assignments',
  initialState: {
    assignments: assignmentsAdapter.getInitialState(),
    jobsiteAssignment: jobsiteAssignmentAdapter.getInitialState(),
    assignment: assignmentAdapter.getInitialState(),
    kpis: {},
  } as AssignmentsState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(listAssignments.pending, (state: AssignmentsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listAssignments: true };
    });
    builder.addCase(listAssignments.fulfilled, (state: AssignmentsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listAssignments: false };
      if (action.payload.data.result) {
        assignmentsAdapter.upsertOne(state.assignments, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listAssignments.rejected, (state: AssignmentsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listAssignments: false };
    });

    builder.addCase(getJobsiteAssignment.pending, (state: AssignmentsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getJobsiteAssignment: true };
    });
    builder.addCase(getJobsiteAssignment.fulfilled, (state: AssignmentsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getJobsiteAssignment: false };
      if (action.payload.data.result) {
        jobsiteAssignmentAdapter.upsertOne(state.jobsiteAssignment, action.payload.data.result);
      } else {
        // result is null — no assignment exists (or the empty one was just deleted).
        // Drop any stale entry cached under this site id so the page renders the empty state.
        jobsiteAssignmentAdapter.removeOne(state.jobsiteAssignment, action.meta.arg.siteId);
      }
    });
    builder.addCase(getJobsiteAssignment.rejected, (state: AssignmentsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getJobsiteAssignment: false };
    });

    builder.addCase(getAssignmentById.pending, (state: AssignmentsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), assignment: true };
    });
    builder.addCase(getAssignmentById.fulfilled, (state: AssignmentsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, assignment: false };
      if (action.payload.data.result) {
        assignmentAdapter.upsertOne(state.assignment, action.payload.data.result);
      }
    });
    builder.addCase(getAssignmentById.rejected, (state: AssignmentsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, assignment: false };
    });

    builder.addCase(lookupAssignmentByItem.pending, (state: AssignmentsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), assignment: true };
    });
    builder.addCase(lookupAssignmentByItem.fulfilled, (state: AssignmentsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, assignment: false };
      if (action.payload.data.result) {
        assignmentAdapter.upsertOne(state.assignment, action.payload.data.result);
      }
    });
    builder.addCase(lookupAssignmentByItem.rejected, (state: AssignmentsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, assignment: false };
    });

    builder.addCase(getVehicleManifest.pending, (state: AssignmentsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getVehicleManifest: true };
    });
    builder.addCase(getVehicleManifest.fulfilled, (state: AssignmentsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getVehicleManifest: false };
      state.manifest = action.payload.data.result;
    });
    builder.addCase(getVehicleManifest.rejected, (state: AssignmentsState, action: AnyAction) => {
      // A 404 (no contractor vehicle) or any failure just renders the empty state.
      state.error = action.error;
      state.manifest = undefined;
      state.fetching = { ...state.fetching, getVehicleManifest: false };
    });

    builder.addCase(getJobSiteStatusBoard.pending, (state: AssignmentsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getJobSiteStatusBoard: true };
    });
    builder.addCase(getJobSiteStatusBoard.fulfilled, (state: AssignmentsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getJobSiteStatusBoard: false };
      state.statusBoard = action.payload.data.result ?? [];
    });
    builder.addCase(getJobSiteStatusBoard.rejected, (state: AssignmentsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getJobSiteStatusBoard: false };
    });

    builder.addCase(getSiteActivityTimeline.pending, (state: AssignmentsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getSiteActivityTimeline: true };
    });
    builder.addCase(getSiteActivityTimeline.fulfilled, (state: AssignmentsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getSiteActivityTimeline: false };
      state.siteTimelines = { ...(state.siteTimelines || {}), [action.meta.arg.siteId]: action.payload.data.result ?? [] };
    });
    builder.addCase(getSiteActivityTimeline.rejected, (state: AssignmentsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getSiteActivityTimeline: false };
    });

    builder.addCase(getAssignmentKpis.pending, (state: AssignmentsState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getAssignmentKpis: true };
    });
    builder.addCase(getAssignmentKpis.fulfilled, (state: AssignmentsState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getAssignmentKpis: false };
      state.kpis = action.payload.data.result;
    });
    builder.addCase(getAssignmentKpis.rejected, (state: AssignmentsState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getAssignmentKpis: false };
    });
  },
});

export const { selectById: selectAssignmentsById } = assignmentsAdapter.getSelectors((state: RootState) => state.assignments.assignments);
export const { selectById: selectJobsiteAssignmentById } = jobsiteAssignmentAdapter.getSelectors((state: RootState) => state.assignments.jobsiteAssignment);
export const { selectById: selectAssignmentById } = assignmentAdapter.getSelectors((state: RootState) => state.assignments.assignment);

export const getJobsiteAssignmentById =
  (siteId = '') =>
  (state: RootState): QueryAssignment | undefined =>
    selectJobsiteAssignmentById(state, siteId);

export const getAssignment =
  (assignmentId = '') =>
  (state: RootState): QueryAssignment | undefined =>
    selectAssignmentById(state, assignmentId);

export const getAssignmentsListSelector = createSelector([selectAssignmentsById], (assignments) => ({
  pagination: getPagination(assignments),
  totals: assignments?.totals,
  result: assignments?.result?.map((assignment) => ({
    ...assignment,
    delivered_at_mapped: assignment.delivered_at ? DateTime.fromISO(assignment.delivered_at).toLocaleString(DateTime.DATETIME_SHORT) : '',
  })),
}));

export const getAssignmentsList =
  (keys: ListAssignmentsQuery) =>
  (state: RootState): AssignmentsTypeMapped =>
    getAssignmentsListSelector(state, keysToId(keys));

export const getAssignmentKpisSelector = createSelector([(state: RootState): QueryAssignmentKPIs => state.assignments.kpis], (kpis) => kpis);

export const getVehicleManifestSelector = (state: RootState): QueryVehicleManifest | undefined => state.assignments.manifest;
export const getVehicleManifestFetching = (state: RootState): boolean => state.assignments.fetching?.getVehicleManifest !== false;

export const getJobSiteStatusBoardSelector = (state: RootState): QuerySiteStatusEntry[] | undefined => state.assignments.statusBoard;
export const getJobSiteStatusBoardFetching = (state: RootState): boolean => state.assignments.fetching?.getJobSiteStatusBoard !== false;

export const getSiteActivityTimelineSelector =
  (siteId = '') =>
  (state: RootState): QuerySiteTimelineEvent[] | undefined =>
    state.assignments.siteTimelines?.[siteId];

export const getJobsiteAssignmentByIdFetching = (state: RootState): boolean => state.assignments.fetching?.getJobsiteAssignment !== false;
export const getAssignmentFetching = (state: RootState): boolean => state.assignments.fetching?.assignment !== false;
export const getAssignmentsFetching = (state: RootState): boolean => state.assignments.fetching?.listAssignments !== false;
export const getAssignmentKpisFetching = (state: RootState): boolean => state.assignments.fetching?.getAssignmentKpis !== false;

export default assignmentsSlice.reducer;
